import React from "react";
import {
  Box,
  VStack,
  SimpleGrid,
  Text,
  Button,
  Divider,
  Input,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  useColorModeValue,
} from "@chakra-ui/react";

const ChargingControls = ({
  mode,
  setMode,
  value,
  setValue,
  mobileNumber,
  setMobileNumber,
  errors,
  setErrors,
  selectedConnector,
  handleStart,
  isStartButtonEnabled,
  startLoading,
}) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const labelColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box
      bg={cardBg}
      borderRadius="xl"
      border="1px solid"
      borderColor={borderColor}
      p={{ base: 3, md: 6 }}
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={{ base: 2, md: 4 }}>
        <Text fontSize={{ base: "sm", md: "lg" }} fontWeight="bold" color={textColor}>
          CHARGING CONTROLS
        </Text>
        
        <Divider />

        {/* Mode Selection */}
        <VStack align="stretch" spacing={{ base: 1, md: 2 }}>
          <Text fontSize={{ base: "2xs", md: "sm" }} fontWeight="semibold" color={labelColor}>
            CHARGING MODE
          </Text>
          <SimpleGrid columns={3} spacing={{ base: 1.5, md: 2 }}>
            {[
              { label: "MONEY", value: "money" },
              { label: "UNITS", value: "units" },
              { label: "TIME", value: "time" },
            ].map(modeOption => (
              <Button
                key={modeOption.value}
                size="md"
                h={{ base: "26px", md: "30px" }}
                bg={mode === modeOption.value ? "#FF5C00" : "transparent"}
                color={mode === modeOption.value ? "white" : "gray.600"}
                border="1px solid"
                borderColor={mode === modeOption.value ? "#FF5C00" : "gray.300"}
                borderRadius="md"
                onClick={() => setMode(modeOption.value)}
                fontSize={{ base: "xs", md: "sm" }}
                _hover={{
                  bg: mode === modeOption.value ? "#e55200" : "rgba(255, 92, 0, 0.1)",
                }}
              >
                {modeOption.label}
              </Button>
            ))}
          </SimpleGrid>
        </VStack>

        {/* Value Input */}
        <FormControl isInvalid={errors.value}>
          <Text fontSize={{ base: "2xs", md: "sm" }} fontWeight="semibold" color={labelColor} mb={2}>
            VALUE
          </Text>
          <Input
            type="number"
            placeholder={`Enter ${mode === "money" ? "amount in rupees" : mode === "units" ? "units" : "time in minutes"}`}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (errors.value) {
                setErrors({ ...errors, value: null });
              }
            }}
            size={{ base: "md", md: "lg" }}
            fontSize={{ base: "xs", md: "sm" }}
            _placeholder={{ fontSize: { base: "xs", md: "sm" } }}
            min="0"
          />
          <FormHelperText fontSize={{ base: "2xs", md: "xs" }}>
            {mode === "money" ? "Enter amount in ₹" : mode === "units" ? "Enter energy in kWh" : "Enter time in minutes"}
          </FormHelperText>
          {errors.value && <FormErrorMessage>{errors.value}</FormErrorMessage>}
        </FormControl>

        {/* Mobile Number Input */}
        <FormControl isInvalid={errors.mobileNumber}>
          <Text fontSize={{ base: "2xs", md: "sm" }} fontWeight="semibold" color={labelColor} mb={2}>
            MOBILE NUMBER
          </Text>
          <Input
            type="tel"
            placeholder="Enter 10-digit mobile number"
            value={mobileNumber}
            onChange={(e) => {
              const input = e.target.value.replace(/\D/g, "");
              if (input.length <= 10) {
                setMobileNumber(input);
                if (errors.mobileNumber) {
                  setErrors({ ...errors, mobileNumber: null });
                }
              }
            }}
            size={{ base: "md", md: "lg" }}
            fontSize={{ base: "xs", md: "sm" }}
            _placeholder={{ fontSize: { base: "xs", md: "sm" } }}
            maxLength={10}
          />
          <FormHelperText fontSize={{ base: "2xs", md: "xs" }}>Enter 10-digit mobile number</FormHelperText>
          {errors.mobileNumber && <FormErrorMessage>{errors.mobileNumber}</FormErrorMessage>}
        </FormControl>

        {/* Warning Messages */}
        {errors.connector && (
          <Text fontSize={{ base: "xs", md: "sm" }} color="red.500" textAlign="center">
            ⚠️ {errors.connector}
          </Text>
        )}
        {selectedConnector && selectedConnector.Status !== "Available" && selectedConnector.Status !== "Preparing" && (
          <Text fontSize={{ base: "xs", md: "sm" }} color="orange.500" textAlign="center">
            ⚠️ Connector is not available for charging
          </Text>
        )}

        {/* Start Button */}
        <Button
          bg="#FF5C00"
          color="white"
          size="lg"
          h={{ base: "44px", md: "48px" }}
          w="full"
          onClick={handleStart}
          isDisabled={!isStartButtonEnabled()}
          isLoading={startLoading}
          borderRadius="md"
          fontSize={{ base: "sm", md: "md" }}
          _hover={{ bg: "#e55200" }}
          _disabled={{
            bg: "gray.300",
            cursor: "not-allowed",
            opacity: 0.6,
          }}
        >
          START CHARGING
        </Button>
      </VStack>
    </Box>
  );
};

export default ChargingControls;
